import { SendHistoryFilterType, useInfiniteAllSendsHistory } from '@ukpostbox/client-data';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import FilterSendsHistoryModal from '../ProfilesAdmin/Components/Modals/FilterSendsHistoryModal';

type SendMailHistoryProviderType = {
	userId?: string;
	pageId?: number;

	children: any;
};

interface SendMailHistoryType {
	filterActive: boolean;
	filterSettings: SendHistoryFilterType;

	status: any;
	data: any;
	error: any;
	isFetchingNextPage: any;
	fetchNextPage: any;
	hasNextPage: any;

	isStale: boolean;
	isRefetching: boolean;

	UpdateFilterActive: any;
	HandleFilterChange: any;
	HandleFilterCleared: any;
	HandleFilterRemoved: any;

	userId?: string;
	pageId?: number;
}

const SendMailHistoryContext = React.createContext<SendMailHistoryType>({} as SendMailHistoryType);

function SendMailHistoryProvider(props: SendMailHistoryProviderType) {
	const { state } = useLocation();
	const navigate = useNavigate();

	const [pageId, setPageId] = useState<number>();
	const [userId, setUserId] = useState<string>();
	const [filterActive, setFilterActive] = useState<boolean>(false);

	const DefaultFilterOptions = () => {
		const defaultFilterOptions: SendHistoryFilterType = {};

		if (props.userId) {
			defaultFilterOptions.userIds = [props.userId];
		} else if (state && state.userIds) {
			defaultFilterOptions.userIds = [state.userIds];
		}

		if (props.pageId) {
			defaultFilterOptions.pageIds = [props.pageId];
		} else if (state && state.pageIds) {
			defaultFilterOptions.userIds = [state.pageIds];
		}

		return defaultFilterOptions;
	};

	const [filterSettings, setFilterSettings] = useState<SendHistoryFilterType>(DefaultFilterOptions());

	const { status, data, error, isFetchingNextPage, fetchNextPage, hasNextPage, isStale, isRefetching } =
		useInfiniteAllSendsHistory(filterSettings);

	const UpdateFilterActive = (filterState: boolean) => {
		setFilterActive(filterState);
	};

	const CheckDefaultFilterValues = (filterData: SendHistoryFilterType) => {
		const newFilterData = window.structuredClone(filterData);

		if (newFilterData.userIds && props.userId) {
			newFilterData.userIds = [...newFilterData.userIds, props.userId];
		} else if (props.userId) {
			newFilterData.userIds = [props.userId];
		}

		if (newFilterData.pageIds && props.pageId) {
			newFilterData.pageIds = [...newFilterData.pageIds, props.pageId];
		} else if (props.pageId) {
			newFilterData.pageIds = [props.pageId];
		}

		return filterData;
	};

	const HandleFilterChange = (filterData: SendHistoryFilterType) => {
		setFilterSettings(CheckDefaultFilterValues(filterData));
	};

	const HandleFilterCleared = () => {
		setFilterSettings(DefaultFilterOptions());
	};

	const HandleFilterRemoved = (key: string) => {
		const newFilters: any = { ...filterSettings };

		delete newFilters[key];

		setFilterSettings(newFilters);
	};

	const sendMailHistoryMemo = useMemo(
		() => ({
			filterActive,
			filterSettings,
			status,
			data,
			error,
			isFetchingNextPage,
			fetchNextPage,
			hasNextPage,
			isStale,
			isRefetching,
			userId,
			pageId,
			UpdateFilterActive,
			HandleFilterChange,
			HandleFilterCleared,
			HandleFilterRemoved,
		}),
		[
			filterActive,
			filterSettings,
			status,
			data,
			error,
			isFetchingNextPage,
			fetchNextPage,
			hasNextPage,
			isStale,
			isRefetching,
			userId,
			pageId,
		],
	);

	useEffect(() => {
		navigate(window.location.pathname, { replace: true });
		if (props.pageId) {
			setPageId(props.pageId);
		}
		if (props.userId) {
			setUserId(props.userId);
		}
	}, []);

	return (
		<SendMailHistoryContext.Provider value={sendMailHistoryMemo}>
			{props.children}

			<FilterSendsHistoryModal
				filterData={filterSettings}
				isOpen={filterActive}
				hidePageDetails={!!props.pageId}
				hideUserDetails={!!props.userId}
				closeCallback={() => UpdateFilterActive(false)}
				actionedCallback={(filterData: SendHistoryFilterType) => {
					HandleFilterChange(filterData);
					UpdateFilterActive(false);
				}}
			/>
		</SendMailHistoryContext.Provider>
	);
}

const useSendMailHistory = (pageId?: number, userId?: string) => useContext(SendMailHistoryContext);

export { SendMailHistoryContext, useSendMailHistory };

export default SendMailHistoryProvider;
